<template>
  <div class="sb-navigation sb-navigation--story">
    <div class="position-relative py-3">
      <div
        v-for="(nav, nav_idx) in disclosures"
        :key="`navigation-group-${nav_idx}-${nav.link}`"
        class="navigation-group"
      >
        <template v-if="nav.type == 'title'">
          <div
            v-if="nav.title_lv === 1"
            :class="['d-flex align-items-center custom-padding-left', { 'mt-3': nav_idx !== 0 }]"
          >
            <h5
              class="nav-title nav-title--parent"
              :data-observe-anchor="'title_' + nav.link"
              :class="{ 'navigation-indicator': nav_idx === 0 }"
            >
              <b-link
                class="nav-anchor text-keep-all text-decoration-none"
                @click="scroll_to_title(nav.link)"
              >
                <span v-show="lang === 'KOR'">{{ nav.title1 }}</span>
                <span v-show="lang === 'ENG'">{{
                  nav.title1_em ? nav.title1_em : nav.title1_ea
                }}</span>
              </b-link>
            </h5>
          </div>
          <div v-if="nav.title_lv === 2">
            <h6
              :class="['nav-title nav-title--child', 'mb-0', 'custom-padding-left']"
              :data-observe-anchor="'title_' + nav.link"
            >
              <b-link
                class="nav-anchor text-keep-all text-decoration-none"
                @click="scroll_to_title(nav.link)"
              >
                <span v-show="lang === 'KOR'">{{ nav.title1 }}</span>
                <span v-show="lang === 'ENG'">{{
                  nav.title1_em ? nav.title1_em : nav.title1_ea
                }}</span>
              </b-link>
            </h6>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'StoryBoardTocViewer',
  props: {
    disclosures: { type: Array, default: null }
  },
  computed: {
    ...mapState('report_list', ['lang']),
    ...mapGetters('story_board', ['selected_lv1', 'selected_lv2']),
    ...mapGetters('report_list', ['selected_report']),
    anchorList() {
      if (!this.disclosures) return [];
      return this.disclosures.reduce((_anchors, navEle) => {
        if (navEle?.title_lv < 3) {
          _anchors.push(`#title_${navEle.link}`);
        }
        return _anchors;
      }, []);
    },
    indicatorColor() {
      return this.selected_lv1?.background_color || this.selected_report?.main_color;
    }
  },
  watch: {
    disclosures_loaded: {
      immediate: true,
      handler(newV) {
        if (newV) {
          setTimeout(() => {
            this.handle_toc_scroll();
          }, 1000);
        }
      }
    },
    disclosures: {
      immediate: true,
      handler(newV) {
        if (newV && newV.length > 0) {
          this.handle_toc_scroll();
        }
      }
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleWindowScroll);
  },
  beforeDestroy() {
    if (this.anchorObserver) {
      this.anchorObserver.disconnect();
      this.anchorObserver = null;
    }
    // setTimeout이 unmount 후 발사되어 새 observer를 만들고 leak되는 race 방지
    if (this.observerTimerId) {
      clearTimeout(this.observerTimerId);
      this.observerTimerId = null;
    }
    window.removeEventListener('scroll', this.handleWindowScroll);
  },
  data() {
    return {
      activatedAnchor: null,
      anchorObserver: null,
      observeDealyTimeSleep: 0,
      // 현재 active zone 안에 들어와 있는 anchor id들 — Set으로 추적해 가장 위쪽을 선택.
      // Vue 2는 Set의 add/delete/clear를 반응형 트리거로 만들지 않으므로 의도적으로 비반응형.
      // 따라서 이 값을 template/computed에서 직접 바인딩하지 말 것.
      intersectingAnchors: new Set(),
      // handle_toc_scroll 내부 setTimeout 핸들 — beforeDestroy에서 clearTimeout 위해 추적
      observerTimerId: null
    };
  },
  methods: {
    setActiveIndicator(anchorId) {
      if (!anchorId || this.activatedAnchor === anchorId) return;

      const _activedNavs = document.querySelectorAll('.navigation-indicator');
      if (_activedNavs.length) {
        _activedNavs.forEach(_activedNav => {
          _activedNav.classList.remove('navigation-indicator');
          _activedNav.style.color = '';
          _activedNav.style.borderLeft = '';
        });
      }

      const _navToAnchor = document.querySelector(`[data-observe-anchor="${anchorId}"]`);
      if (_navToAnchor) {
        this.activatedAnchor = anchorId;
        const color = this.indicatorColor;
        if (color) {
          const node = _navToAnchor.childNodes[0];
          node.classList.add('navigation-indicator');
          node.style.color = color;
          node.style.borderLeft = `4px solid ${color}`;
        }
      }
    },

    handlerObserveAnchor(entries) {
      // 들어오고 나간 항목을 Set에 누적
      entries.forEach(entry => {
        const anchorId = entry.target.id.replace(/#/g, '');
        if (entry.isIntersecting) {
          this.intersectingAnchors.add(anchorId);
        } else {
          this.intersectingAnchors.delete(anchorId);
        }
      });

      if (this.intersectingAnchors.size === 0) return;

      // 여러 제목이 동시 intersect일 때 anchorList(=DOM) 순서상 가장 위쪽을 선택.
      // → 가까운 두 제목 중 위쪽을 클릭했을 때 아래쪽 제목이 indicator를 가져가는 문제 방지
      for (const anchor of this.anchorList) {
        const id = anchor.replace('#', '');
        if (this.intersectingAnchors.has(id)) {
          this.setActiveIndicator(id);
          break;
        }
      }
    },

    // 윈도우 스크롤 핸들러: IntersectionObserver가 못 잡는 양 끝점(최상/최하단)을 직접 처리
    handleWindowScroll() {
      if (!this.anchorList || this.anchorList.length === 0) return;

      // 허용 오차 ↑: 스크롤바 미세 오차, 브라우저 차이를 흡수
      const atTop = window.scrollY <= 5;
      const atBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 5;

      if (atTop) {
        const firstAnchorId = this.anchorList[0].replace('#', '');
        this.setActiveIndicator(firstAnchorId);
      } else if (atBottom) {
        const lastAnchorId = this.anchorList[this.anchorList.length - 1].replace('#', '');
        this.setActiveIndicator(lastAnchorId);
      }
    },

    handle_toc_scroll() {
      if (!this.anchorList) return;
      this.observeDealyTimeSleep = 1000;

      // 이전 호출의 timer가 살아있으면 먼저 취소 (disclosures 빠른 연속 갱신 시 leak 방지)
      if (this.observerTimerId) {
        clearTimeout(this.observerTimerId);
      }
      this.observerTimerId = setTimeout(() => {
        this.observerTimerId = null;
        // disclosures가 갱신될 때 기존 observer + 추적 set을 함께 리셋
        if (this.anchorObserver) {
          this.anchorObserver.disconnect();
          this.anchorObserver = null;
        }
        this.intersectingAnchors.clear();
        // active zone 상단을 105px(scrollMarginTop 100px + 여유 5px)에 고정.
        // → 클릭으로 안착한 제목은 zone에 포함되고, 바로 위 이전 제목은 배제된다.
        // 하단은 viewport의 40% 지점(=상단부터 40%)까지 → 좁은 단일 zone으로 가까운 제목 동시 매칭 시 topmost 선택과 결합.
        this.anchorObserver = new IntersectionObserver(this.handlerObserveAnchor, {
          rootMargin: '-105px 0px -60% 0px',
          threshold: 0
        });
        this.anchorList.forEach(_anchor => {
          const _anchorTitle = document.querySelector(_anchor);
          if (_anchorTitle) {
            this.anchorObserver.observe(_anchorTitle);
          }
        });
        const initialNav = document.querySelector('.navigation-indicator');
        const color = this.indicatorColor;
        if (initialNav && color) {
          initialNav.style.color = color;
          initialNav.style.borderLeft = `4px solid ${color}`;
        }
      }, this.observeDealyTimeSleep);
    },
    scroll_to_title(link) {
      const element = document.getElementById('title_' + link);
      if (element) {
        element.style.scrollMarginTop = '100px';
        element.scrollIntoView({ behavior: 'auto' });
        history.pushState(null, null, '#title_' + link);
        // 클릭한 제목으로 indicator 즉시 이동 (IntersectionObserver 트리거 전에)
        this.setActiveIndicator('title_' + link);
      }
    }
  }
};
</script>

<style scoped>
.custom-padding-left {
  padding-left: 15px;
  padding-right: 0px;
}
</style>
